import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ArrowLeft,
  Building2,
  CheckCircle2,
  CreditCard,
  FileText,
  KeyRound,
  Mail,
  MapPin,
  Phone,
  Power,
  RefreshCw,
  Smartphone,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import GeneratedAvatar from "@/components/common/GeneratedAvatar";
import StatusBadge from "@/components/common/StatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import {
  createAdminShopNote,
  getAdminShop,
  grantShopSubscriptionAccess,
  reactivateAdminShop,
  resetAdminShopAccess,
  suspendAdminShop,
} from "@/lib/api";
import { fmtDate, timeAgo } from "@/lib/formatters";
import { formatMoneyMinor } from "@/pages/overviewModel";

const TABS = ["overview", "branches", "staff", "subscription", "payments", "usage", "notes"];

function DetailSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading shop">
      <Skeleton className="h-10 w-36 rounded-lg" />
      <Skeleton className="h-60 w-full rounded-xl" />
      <Skeleton className="h-11 w-full max-w-3xl rounded-xl" />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-28 rounded-xl" />)}
      </div>
    </div>
  );
}

function EmptyRows({ columns, children }) {
  return <TableRow><TableCell colSpan={columns} className="py-12 text-center text-[#6B7280]">{children}</TableCell></TableRow>;
}

export default function ShopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentAdmin } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") || "overview";
  const activeTab = TABS.includes(requestedTab) ? requestedTab : "overview";
  const [reason, setReason] = useState("");
  const [noteBody, setNoteBody] = useState("");

  const { data, error, isError, isLoading, refetch } = useQuery({
    queryKey: ["admin-shop", id],
    queryFn: ({ signal }) => getAdminShop(id, signal),
    enabled: Boolean(id),
  });
  const shop = data?.shop;
  const canOperate = currentAdmin?.permissions?.includes("shops.manage");

  const refreshRelated = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-shop", id] }),
      queryClient.invalidateQueries({ queryKey: ["admin-shops"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-overview"] }),
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] }),
    ]);
  };

  const lifecycle = useMutation({
    mutationFn: ({ suspend, mutationReason }) => (
      suspend ? suspendAdminShop(id, mutationReason) : reactivateAdminShop(id, mutationReason)
    ),
    onSuccess: async (_, variables) => {
      setReason("");
      await refreshRelated();
      toast.success(variables.suspend ? "Shop suspended and active sessions revoked" : "Shop reactivated");
    },
    onError: (mutationError) => toast.error(mutationError.message),
  });

  const resetAccess = useMutation({
    mutationFn: (mutationReason) => resetAdminShopAccess(id, mutationReason),
    onSuccess: async () => {
      setReason("");
      await refreshRelated();
      toast.success(`Reset email sent to ${shop?.ownerEmail}`);
    },
    onError: (mutationError) => toast.error(mutationError.message),
  });

  const grantAccess = useMutation({
    mutationFn: () => grantShopSubscriptionAccess(id, {
      days: 30,
      reason: "Granted from the admin shop detail",
    }),
    onSuccess: async () => {
      await refreshRelated();
      toast.success("Access granted for 30 days");
    },
    onError: (mutationError) => toast.error(mutationError.message),
  });

  const addNote = useMutation({
    mutationFn: () => createAdminShopNote(id, noteBody),
    onSuccess: async () => {
      setNoteBody("");
      await queryClient.invalidateQueries({ queryKey: ["admin-shop", id] });
      toast.success("Internal note saved");
    },
    onError: (mutationError) => toast.error(mutationError.message),
  });

  if (isLoading) return <DetailSkeleton />;
  if (isError || !shop) {
    return (
      <div className="card-elev rounded-xl py-20 text-center" role="alert">
        <p className="font-display text-lg font-semibold text-[#064E3B]">
          {error?.status === 404 ? "Shop not found" : "Unable to load this shop"}
        </p>
        <p className="mt-1 text-sm text-[#6B7280]">{error?.message}</p>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" onClick={() => navigate("/shops")}>Back to shops</Button>
          {error?.status !== 404 && <Button onClick={() => refetch()} className="bg-[#0E5B52]"><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>}
        </div>
      </div>
    );
  }

  const isSuspended = shop.adminStatus === "Suspended";

  return (
    <div data-testid="shop-detail-page" className="space-y-6">
      <Button variant="ghost" className="-ml-3 text-[#0E5B52]" onClick={() => navigate("/shops")}>
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to shops
      </Button>

      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#0E5B52] to-[#0A3934] p-7 text-white">
        <div className="relative z-10 flex flex-col gap-6 md:flex-row">
          <GeneratedAvatar seed={shop.ownerId || shop.id} name={shop.ownerName} className="h-20 w-20 ring-4 ring-white/10" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-2xl font-semibold">{shop.name}</h1>
              <StatusBadge status={shop.accessStatus} />
              <StatusBadge status={shop.syncStatus} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/80">
              {shop.ownerPhone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{shop.ownerPhone}</span>}
              {shop.ownerEmail && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{shop.ownerEmail}</span>}
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{shop.location}</span>
            </div>
            <div className="mt-5 grid max-w-2xl grid-cols-2 gap-4 border-t border-white/10 pt-4 md:grid-cols-4">
              <HeaderMetric label="Plan" value={shop.planName} />
              <HeaderMetric label="MRR" value={shop.mrrMinor == null ? "Restricted" : formatMoneyMinor(shop.mrrMinor, shop.currencyCode)} />
              <HeaderMetric label="Branches" value={shop.branchCount} />
              <HeaderMetric label="Devices" value={shop.usage.activeDevices} />
            </div>
          </div>
          {canOperate && <div className="flex flex-col gap-2 self-start">
            <ReasonDialog
              title="Reset owner access?"
              description={`A recovery code will be sent to ${shop.ownerEmail || "the owner"} and active sessions will be revoked.`}
              label="Send reset email"
              icon={KeyRound}
              reason={reason}
              setReason={setReason}
              pending={resetAccess.isPending}
              onConfirm={() => resetAccess.mutate(reason)}
            />
            <ReasonDialog
              title={isSuspended ? `Reactivate ${shop.name}?` : `Suspend ${shop.name}?`}
              description={isSuspended ? "The shop can sign in again, subject to subscription access." : "The shop will lose access immediately and active sessions will be revoked."}
              label={isSuspended ? "Reactivate" : "Suspend"}
              icon={isSuspended ? CheckCircle2 : Power}
              danger={!isSuspended}
              reason={reason}
              setReason={setReason}
              pending={lifecycle.isPending}
              onConfirm={() => lifecycle.mutate({ suspend: !isSuspended, mutationReason: reason })}
            />
          </div>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(tab) => setSearchParams(tab === "overview" ? {} : { tab })}>
        <TabsList className="h-auto flex-wrap rounded-xl border border-[#0E5B52]/10 bg-white p-1">
          {TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab} className="capitalize rounded-lg data-[state=active]:bg-[#0E5B52] data-[state=active]:text-white">
              {tab}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <InfoCard icon={Activity} label="Sync health" value={shop.syncStatus} hint={`${shop.usage.activeSyncConflicts} active conflicts`} />
            <InfoCard icon={Smartphone} label="Last login" value={shop.lastLoginAt ? timeAgo(shop.lastLoginAt) : "Never"} hint={`${shop.usage.activeDevices} active devices`} />
            <InfoCard icon={Building2} label="Created" value={fmtDate(shop.createdAt)} hint={`${shop.branchCount} active branches`} />
            <InfoCard icon={CreditCard} label="Period ends" value={shop.currentPeriodEnd ? fmtDate(shop.currentPeriodEnd) : "Not scheduled"} hint={`${shop.planName} plan`} />
            <InfoCard icon={Users} label="Active staff" value={shop.staffCount} hint={`${shop.staff.length} total staff records`} />
            <InfoCard icon={Activity} label="30-day sales" value={shop.usage.salesLast30Days} hint={`${shop.usage.activeProducts} active products`} />
          </div>
        </TabsContent>

        <TabsContent value="branches" className="mt-5">
          <DataTable headers={["Branch", "Location", "Staff", "Last activity"]}>
            {shop.branches.length === 0 ? <EmptyRows columns={4}>No branches have been created.</EmptyRows> : shop.branches.map((branch) => (
              <TableRow key={branch.id}>
                <TableCell className="font-semibold text-[#064E3B]">{branch.name}{!branch.isActive && " (inactive)"}</TableCell>
                <TableCell>{branch.location}</TableCell>
                <TableCell>{branch.staffCount}</TableCell>
                <TableCell>{branch.lastSyncAt ? timeAgo(branch.lastSyncAt) : "Never"}</TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        <TabsContent value="staff" className="mt-5">
          <DataTable headers={["Name", "Role", "Branch", "Status", "Last seen"]}>
            {shop.staff.length === 0 ? <EmptyRows columns={5}>No staff members have been added.</EmptyRows> : shop.staff.map((member) => (
              <TableRow key={member.id}>
                <TableCell><div className="font-semibold text-[#064E3B]">{member.name}</div><div className="text-xs text-[#6B7280]">{member.email}</div></TableCell>
                <TableCell>{member.role}</TableCell><TableCell>{member.branchName}</TableCell>
                <TableCell><StatusBadge status={member.status.toLowerCase()} /></TableCell>
                <TableCell>{member.lastSeenAt ? timeAgo(member.lastSeenAt) : "Never"}</TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        <TabsContent value="subscription" className="mt-5">
          <div className="card-elev rounded-xl p-5">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <div><Label>Current plan</Label><Value>{shop.planName}</Value><p className="text-sm text-[#6B7280]">{shop.currencyCode}</p></div>
              <div><Label>Access status</Label><div className="mt-2"><StatusBadge status={shop.accessStatus} /></div><p className="mt-1 text-sm text-[#6B7280]">{shop.currentPeriodEnd ? `Ends ${fmtDate(shop.currentPeriodEnd)}` : "No period end"}</p></div>
              {canOperate && <div className="flex items-start md:justify-end">
                <Button disabled={grantAccess.isPending} onClick={() => grantAccess.mutate()} className="bg-[#0E5B52] hover:bg-[#0A4640]">
                  Grant 30 days
                </Button>
              </div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-5">
          <DataTable headers={["Date", "Reference", "Method", "Status", "Amount"]}>
            {shop.payments.length === 0 ? <EmptyRows columns={5}>No subscription payment attempts yet.</EmptyRows> : shop.payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{fmtDate(payment.createdAt)}</TableCell>
                <TableCell className="font-mono text-xs">{payment.reference || "—"}</TableCell>
                <TableCell className="capitalize">{payment.method}</TableCell>
                <TableCell><StatusBadge status={payment.status} /></TableCell>
                <TableCell className="font-semibold text-[#064E3B]">{formatMoneyMinor(payment.amountMinor, payment.currencyCode)}</TableCell>
              </TableRow>
            ))}
          </DataTable>
        </TabsContent>

        <TabsContent value="usage" className="mt-5">
          <div className="card-elev rounded-xl p-5">
            <Label>Authoritative usage signals</Label>
            <div className="mt-5 grid grid-cols-2 gap-5 md:grid-cols-4">
              <UsageStat label="Active products" value={shop.usage.activeProducts} />
              <UsageStat label="Sales / 30 days" value={shop.usage.salesLast30Days} />
              <UsageStat label="Sync conflicts" value={shop.usage.activeSyncConflicts} />
              <UsageStat label="Active devices" value={shop.usage.activeDevices} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-5">
          <div className="card-elev rounded-xl p-5">
            <Label><FileText className="mr-1.5 inline h-3.5 w-3.5" />Internal notes</Label>
            {canOperate && (
              <>
                <Textarea value={noteBody} onChange={(event) => setNoteBody(event.target.value)} maxLength={4000} rows={4} placeholder="Add operational context visible only to administrators…" className="mt-3 rounded-xl bg-[#F0F6F1]/40" />
                <div className="mt-3 flex justify-end">
                  <Button disabled={!noteBody.trim() || addNote.isPending} onClick={() => addNote.mutate()} className="bg-[#0E5B52]">Save note</Button>
                </div>
              </>
            )}
            <div className="mt-5 space-y-3 border-t border-[#0E5B52]/10 pt-5">
              {shop.notes.length === 0 ? <div className="rounded-xl bg-[#F0F6F1]/60 py-10 text-center text-sm text-[#6B7280]">No internal notes yet.</div> : shop.notes.map((note) => (
                <div key={note.id} className="rounded-xl border border-[#0E5B52]/10 p-4">
                  <p className="whitespace-pre-wrap text-sm text-[#0F2A26]">{note.body}</p>
                  <div className="mt-2 text-xs text-[#6B7280]">{note.authorName} · {timeAgo(note.createdAt)}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReasonDialog({ title, description, label, icon: Icon, reason, setReason, pending, onConfirm, danger }) {
  return (
    <AlertDialog onOpenChange={(open) => { if (!open) setReason(""); }}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className={`rounded-xl border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white ${danger ? "border-red-300/40 text-red-200" : ""}`}>
          <Icon className="mr-1.5 h-4 w-4" /> {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{description}</AlertDialogDescription></AlertDialogHeader>
        <Textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="Administrative reason (required)" />
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={reason.trim().length < 3 || pending} onClick={onConfirm} className={danger ? "bg-red-600 hover:bg-red-700" : "bg-[#0E5B52] hover:bg-[#0A4640]"}>
            {label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DataTable({ headers, children }) {
  return (
    <div className="card-elev overflow-hidden rounded-xl">
      <Table><TableHeader><TableRow className="bg-[#F0F6F1]/60 hover:bg-[#F0F6F1]/60">{headers.map((header) => <TableHead key={header}>{header}</TableHead>)}</TableRow></TableHeader><TableBody>{children}</TableBody></Table>
    </div>
  );
}

function HeaderMetric({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div><div className="mt-0.5 font-display text-lg font-bold">{value}</div></div>;
}

function InfoCard({ icon: Icon, label, value, hint }) {
  return <div className="card-elev rounded-xl p-4"><div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4B5563]"><Icon className="h-3.5 w-3.5 text-[#0E5B52]" />{label}</div><div className="mt-2 font-display text-xl font-bold capitalize text-[#064E3B]">{value}</div><div className="mt-0.5 text-xs text-[#9CA3AF]">{hint}</div></div>;
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-[#4B5563]">{children}</div>;
}

function Value({ children }) {
  return <div className="mt-1 font-display text-xl font-semibold text-[#064E3B]">{children}</div>;
}

function UsageStat({ label, value }) {
  return <div className="rounded-xl bg-[#F0F6F1]/60 p-4"><Label>{label}</Label><Value>{value}</Value></div>;
}
